/* eslint-disable react/no-array-index-key */
import React, { Fragment, useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { bem } from 'helpers/class-name';
import Textarea from 'components/Textarea';
import './GrowingTextarea.scss';

const { block, element } = bem('growing-textarea');

/*
  This component works by rendering the contents of the textarea into an div,
  measuring the dimensions of that div, and then updating the textarea height
  accordingly.
*/
const GrowingTextarea = forwardRef(({ value, heightInLines = 2, viewportWidth, ...rest }, ref) => {
  const [ready, setReady] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(null);
  const [textareaHeightPadding, setTextareaHeightPadding] = useState(null);
  const [textareaWidthPadding, setTextareaWidthPadding] = useState(null);
  const [singleLineHeight, setSingleLineHeight] = useState(null);

  const textarea = useRef();
  const measurement = useRef();
  const setRefs = useCallback(node => {
    textarea.current = node;
    if (ref) {
      ref.current = node;
    }
  });

  // avoid passing redux-form props on to textarea
  const { dispatch, input, meta, ...textareaProps } = rest;

  useEffect(() => {
    if (textarea.current) {
      const {
        borderRightWidth,
        borderLeftWidth,
        paddingRight,
        paddingLeft,
        borderTopWidth,
        borderBottomWidth,
        paddingTop,
        paddingBottom,
      } = window.getComputedStyle(textarea.current);
      const [widthAdjustment, heightAdjustment] = [
        [borderLeftWidth, borderRightWidth, paddingLeft, paddingRight],
        [borderTopWidth, borderBottomWidth, paddingTop, paddingBottom],
      ].map(values => values.reduce((memo, pixels) => memo + parseInt(pixels, 10), 0));

      setTextareaWidthPadding(widthAdjustment);
      setTextareaHeightPadding(heightAdjustment);
    }

    if (measurement.current) {
      const { height } = measurement.current.getBoundingClientRect();
      setSingleLineHeight(height);
    }
  }, [textarea, viewportWidth]); // Recalculate when the viewport width changes (e.g. user resizes their browser)

  useEffect(() => {
    if (textareaWidthPadding && measurement.current) {
      const { height } = measurement.current.getBoundingClientRect();
      setTextareaHeight(height);
      setReady(true);
    }
  }, [measurement, textareaWidthPadding, value]);

  const textToMeasure = textareaWidthPadding ? (
    value.split('\n').map((p, index) => (
      <Fragment key={index}>
        {p}
        <br />
      </Fragment>
    ))
  ) : (
    // used only on initial render to determine the height of a single line, which is used to support the heighInLines prop
    <br />
  );

  return (
    <div className={block({ ready })}>
      <Textarea
        ref={setRefs}
        value={value}
        style={{ height: textareaHeight + textareaHeightPadding }}
        {...textareaProps}
      />
      <div
        ref={measurement}
        className={element('measurement')}
        style={{ marginRight: textareaWidthPadding, minHeight: singleLineHeight * heightInLines }}>
        {textToMeasure}
      </div>
    </div>
  );
});

GrowingTextarea.propTypes = {
  viewportWidth: PropTypes.number,
  heightInLines: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default GrowingTextarea;
